import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  Users, 
  Clock, 
  Target,
  BarChart3,
  FolderIcon,
  AlertCircle,
  Grid3X3,
  Kanban,
  RefreshCw,
  Tag
} from 'lucide-react';
import { KanbanBoard } from '@/components/kanban-board';
import { getTagColors } from '@/lib/tag-colors';
import { getStatusColors } from '@/lib/status-colors';
import { clearAllCaches } from '@/lib/notion-client';
import { format } from 'date-fns';
import type { Task } from '@/lib/types';

interface Project {
  id: string;
  name: string;
  status?: string;
  description?: string;
  created_time?: string;
  last_edited_time?: string;
  url?: string;
  task_count?: number;
  budget?: number;
  properties?: Record<string, any>;
}

interface ProjectTasksResponse {
  tasks?: Task[];
  meta?: {
    total: number;
    count: number;
  };
  _debug?: Record<string, any>;
}

interface ProjectStats {
  totalTasks: number;
  tasksByStatus: Record<string, number>;
  completionRate: number;
  averageTaskDuration?: number;
  budget?: number;
  timeSpent?: number;
}

export default function ProjectPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [taskViewMode, setTaskViewMode] = useState<"kanban" | "grid">("kanban");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const fetchProject = async (force: boolean = false) => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch projects using the same endpoint as ProjectsPage
      const params = new URLSearchParams();
      if (force) {
        params.append('force', '1');
        // Add cache buster to prevent browser/CDN caching
        params.append('_t', Date.now().toString());
      }

      const url = `/wp-json/kj-cockpit/v1/fetch-projects?${params.toString()}`;
      const wp = (window as any).WP_COCKPIT;
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };

      if (wp?.restNonce) {
        headers['X-WP-Nonce'] = wp.restNonce;
      }

      const fetchOptions: RequestInit = { 
        method: 'GET',
        headers 
      };

      fetchOptions.credentials = 'include';

      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data?.projects) {
        const foundProject = data.projects.find((p: Project) => p.id === projectId);
        if (foundProject) {
          setProject(foundProject);
        } else {
          setError('Project not found');
        }
      } else {
        setError('No projects data available');
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectTasks = async (force: boolean = false) => {
    if (!projectId) return;
    
    try {
      const params = new URLSearchParams();
      params.append('projectId', projectId);
      
      if (force) {
        params.append('force', '1');
        params.append('_t', Date.now().toString());
      }

      const url = `/wp-json/kj-cockpit/v1/fetch-project-tasks?${params.toString()}`;
      
      const headers: Record<string, string> = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      };
      
      // Add WP REST nonce header
      const wp = (window as any).WP_COCKPIT;
      const isDev = wp?.devMode || false;
      if (isDev) {
        console.log('[fetchProjectTasks] Project ID:', projectId);
        console.log('[fetchProjectTasks] Force refresh:', force);
        console.log('[fetchProjectTasks] URL:', url);
      }
      if (wp && wp.restNonce) {
        headers['X-WP-Nonce'] = String(wp.restNonce);
        if (isDev) console.log('[fetchProjectTasks] Added nonce to headers');
      } else {
        console.error('No restNonce found in WP_COCKPIT');
      }
      
      const fetchOptions: RequestInit = { 
        credentials: 'same-origin', 
        headers 
      };
      if (force) {
        fetchOptions.cache = 'no-store';
      }
      
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[fetchProjectTasks] Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ProjectTasksResponse = await response.json();
      
      if (isDev) console.log('[fetchProjectTasks] Response data:', data);
      
      if (data.tasks && Array.isArray(data.tasks)) {
        if (isDev) {
          console.log('[fetchProjectTasks] Setting tasks:', data.tasks);
          console.log('[fetchProjectTasks] Task statuses found:', [...new Set(data.tasks.map(t => t.status))]);
        }
        // Normalize task data to match Task interface
        const normalizedTasks = data.tasks.map((t: any) => ({
          id: String(t.id),
          title: t.title ?? t.name ?? '',
          status: t.status ?? 'Not Started',
          due: (t.dueDate || t.due) ? new Date(t.dueDate || t.due) : null,
          contributors: Array.isArray(t.contributors) ? t.contributors.map(String) : [],
          description: t.description ?? t.desc ?? '',
          estimatedHours: t.estimatedHours ? parseFloat(t.estimatedHours) : (t.estimated_hours ? parseFloat(t.estimated_hours) : undefined),
          realisedHours: t.realisedHours ? parseFloat(t.realisedHours) : (t.realised_hours ? parseFloat(t.realised_hours) : undefined),
          tags: Array.isArray(t.tags) ? t.tags.map(String) : [],
          url: t.url || t.notion_url || undefined,
          markdownContent: t.markdownContent || t.markdown_content || undefined,
        }));
        setTasks(normalizedTasks);
      } else {
        if (isDev) console.log('[fetchProjectTasks] No tasks in response');
        setTasks([]);
      }
    } catch (err) {
      console.error('Error fetching project tasks:', err);
      setTasks([]);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchProjectTasks();
  }, [projectId]);

  const calculateProjectStats = (): ProjectStats => {
    const projectTasks = tasks; // Filter by project when that relationship exists
    
    // Define status groups for stats calculation (matching the task kanban order)
    const statusGroups = {
      "To-do": ["Planned", "Backlog"],
      "In Progress": ["Quality Check", "In Progress", "Maintenance"], 
      "Completed": ["Done", "Archived"]
    };

    const tasksByStatus = {
      "To-do": projectTasks.filter(t => t.status && statusGroups["To-do"].includes(t.status)).length,
      "In Progress": projectTasks.filter(t => t.status && statusGroups["In Progress"].includes(t.status)).length,
      "Completed": projectTasks.filter(t => t.status && statusGroups["Completed"].includes(t.status)).length,
    };

    const totalTasks = projectTasks.length;
    const completedTasks = tasksByStatus["Completed"];
    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    return {
      totalTasks,
      tasksByStatus,
      completionRate,
      budget: project?.budget,
    };
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleTaskSelect = (task: Task | null) => {
    if (task) {
      // Navigate to TaskPage with task data in state
      navigate(`/task/${task.id}`, { state: { task } });
    } else {
      setSelectedTask(null);
    }
  };

  const stats = calculateProjectStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-6"></div>
            <div className="grid gap-6 md:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error || 'Project not found'}</AlertDescription>
          </Alert>
          <Button 
            onClick={() => navigate('/projects')} 
            variant="outline" 
            className="mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            onClick={() => navigate('/projects')} 
            variant="outline" 
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <FolderIcon className="h-8 w-8 text-blue-600" />
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  {project.name}
                </h1>
                {project.status && (
                  <Badge className={`${getStatusColors(project.status).bg} ${getStatusColors(project.status).text} ${getStatusColors(project.status).border} border`}>
                    {project.status}
                  </Badge>
                )}
              </div>
              {project.description && (
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  {project.description}
                </p>
              )}
            </div>
            
            <Button onClick={() => { 
              clearAllCaches(); // Clear all caches before refreshing
              fetchProject(true); 
              fetchProjectTasks(true); 
            }}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 h-12 bg-gray-100 dark:bg-gray-700">
            <TabsTrigger value="overview" className="gap-2">
              <Target className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2">
              <FolderIcon className="h-4 w-4" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="stats" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Statistics
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {/* Quick Stats */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalTasks}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.completionRate.toFixed(1)}%</div>
                </CardContent>
              </Card>

              {project.budget && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Budget</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">${project.budget.toLocaleString()}</div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.tasksByStatus["In Progress"]}</div>
                </CardContent>
              </Card>
            </div>

            <Separator className="my-6" />

            {/* Project Details */}
            <Card>
              <CardHeader>
                <CardTitle>Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  {project.created_time && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Created:</span>
                      <span className="text-sm">{formatDate(project.created_time)}</span>
                    </div>
                  )}
                  
                  {project.last_edited_time && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Last Updated:</span>
                      <span className="text-sm">{formatDate(project.last_edited_time)}</span>
                    </div>
                  )}

                  {project.task_count !== undefined && (
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Task Count:</span>
                      <span className="text-sm">{project.task_count}</span>
                    </div>
                  )}

                  {project.budget && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Budget:</span>
                      <span className="text-sm">${project.budget.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <div className="space-y-6">
              {/* Tasks Header with View Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Project Tasks</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {stats.totalTasks} tasks • {stats.tasksByStatus["Completed"]} completed
                  </p>
                </div>
                
                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  <Button
                    variant={taskViewMode === "kanban" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setTaskViewMode("kanban")}
                    className="h-8 px-3"
                  >
                    <Kanban className="h-4 w-4 mr-1" />
                    Kanban
                  </Button>
                  <Button
                    variant={taskViewMode === "grid" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setTaskViewMode("grid")}
                    className="h-8 px-3"
                  >
                    <Grid3X3 className="h-4 w-4 mr-1" />
                    Grid
                  </Button>
                </div>
              </div>

              {/* Tasks Content */}
              {taskViewMode === "kanban" ? (
                <KanbanBoard 
                  tasks={tasks} 
                  selectedTask={selectedTask}
                  onSelectTask={handleTaskSelect}
                  enableNavigation={true}
                />
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {tasks.map((task) => (
                    <Card 
                      key={task.id} 
                      className={`border-l-4 cursor-pointer hover:shadow-lg transition-shadow ${
                        selectedTask?.id === task.id ? 'ring-2 ring-blue-500' : ''
                      }`}
                      style={{ borderLeftColor: getStatusColors(task.status).borderColorValue }}
                      onClick={() => handleTaskSelect(task)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <CardTitle className="text-lg">{task.title}</CardTitle>
                          <Badge className={`${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border`}>
                            {task.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          {task.due && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Calendar className="h-4 w-4" />
                              <span>Due: {formatDate(task.due.toString())}</span>
                            </div>
                          )}
                          {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                                <span>{task.realisedHours.toFixed(1)}h / {task.estimatedHours}h</span>
                              ) : task.realisedHours !== undefined ? (
                                <span>{task.realisedHours.toFixed(1)}h tracked</span>
                              ) : (
                                <span>Est: {task.estimatedHours}h</span>
                              )}
                            </div>
                          )}
                          {task.assignee && task.assignee.length > 0 && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="h-4 w-4" />
                              <span className="font-medium">
                                {task.assignee.length} assignee{task.assignee.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                          {task.contributors && task.contributors.length > 0 && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Users className="h-4 w-4" />
                              <span>{task.contributors.length} contributor{task.contributors.length > 1 ? 's' : ''}</span>
                            </div>
                          )}
                          {task.tags && task.tags.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Tag className="h-4 w-4 text-muted-foreground" />
                              <div className="flex gap-1 flex-wrap">
                                {task.tags.slice(0, 3).map((tag, index) => {
                                  const tagColors = getTagColors(tag);
                                  return (
                                    <Badge key={index} className={`text-xs ${tagColors.bg} ${tagColors.text} ${tagColors.border} border`}>
                                      {tag}
                                    </Badge>
                                  );
                                })}
                                {task.tags.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{task.tags.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                          {task.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-2">
                              {task.description}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {tasks.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Target className="h-12 w-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                    <p className="text-gray-500 dark:text-gray-400">No tasks found for this project</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats">
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Status Distribution */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">To-do</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.tasksByStatus["To-do"]}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalTasks > 0 ? ((stats.tasksByStatus["To-do"] / stats.totalTasks) * 100).toFixed(1) : 0}%
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.tasksByStatus["In Progress"]}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalTasks > 0 ? ((stats.tasksByStatus["In Progress"] / stats.totalTasks) * 100).toFixed(1) : 0}%
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium">Completed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.tasksByStatus["Completed"]}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalTasks > 0 ? ((stats.tasksByStatus["Completed"] / stats.totalTasks) * 100).toFixed(1) : 0}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Progress Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Project Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Overall Completion</span>
                        <span className="text-sm text-muted-foreground">{stats.completionRate.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${stats.completionRate}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Task Distribution</h4>
                        <div className="space-y-2">
                          {Object.entries(stats.tasksByStatus).map(([status, count]) => (
                            <div key={status} className="flex items-center justify-between text-sm">
                              <span>{status}</span>
                              <span>{count} tasks</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {project.budget && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Budget Information</h4>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span>Total Budget</span>
                              <span>${project.budget.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span>Cost per Task</span>
                              <span>${stats.totalTasks > 0 ? (project.budget / stats.totalTasks).toFixed(0) : 0}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}